import { apiDelete, apiGet, apiPost, apiPut } from "../client";
import type {
  ApproveScenarioRequestApi,
  CreateScenarioChangeRequestApi,
  CreateScenarioCommentRequestApi,
  CreateScenarioDependencyRequestApi,
  CreateScenarioRequestApi,
  CreateScenarioVersionRequestApi,
  RejectScenarioRequestApi,
  RequestChangesRequestApi,
  ScenarioApprovalResponseApi,
  ScenarioChangeListResponseApi,
  ScenarioChangeResponseApi,
  ScenarioCommentListResponseApi,
  ScenarioCommentResponseApi,
  ScenarioDependencyResponseApi,
  ScenarioForecastResponseApi,
  ScenarioListResponseApi,
  ScenarioMetricsResponseApi,
  ScenarioResponseApi,
  ScenarioVersionResponseApi,
  ShareScenarioRequestApi,
  UpdateScenarioChangeRequestApi,
  UpdateScenarioRequestApi,
  UpdateScenarioShareRequestApi,
} from "../models/scenario";

// Scenario CRUD
export async function getScenarios(): Promise<ScenarioListResponseApi> {
  return apiGet<ScenarioListResponseApi>("scenarios");
}

export async function getScenarioById(
  scenarioId: number,
): Promise<ScenarioResponseApi> {
  return apiGet<ScenarioResponseApi>(`scenarios/${scenarioId}`);
}

export async function createScenario(
  request: CreateScenarioRequestApi,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>("scenarios", request);
}

export async function updateScenario(
  scenarioId: number,
  request: UpdateScenarioRequestApi,
): Promise<ScenarioResponseApi> {
  return apiPut<ScenarioResponseApi>(`scenarios/${scenarioId}`, request);
}

export async function deleteScenario(scenarioId: number): Promise<void> {
  return apiDelete<void>(`scenarios/${scenarioId}`);
}

// Clone a scenario — duplicates its adjustments onto a new draft and re-runs the engine.
export async function cloneScenario(
  scenarioId: number,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(`scenarios/${scenarioId}/clone`, {});
}

// Scenario Changes CRUD
export async function getScenarioChanges(
  scenarioId: number,
): Promise<ScenarioChangeListResponseApi> {
  return apiGet<ScenarioChangeListResponseApi>(
    `scenarios/${scenarioId}/changes`,
  );
}

export async function createScenarioChange(
  scenarioId: number,
  request: CreateScenarioChangeRequestApi,
): Promise<ScenarioChangeResponseApi> {
  return apiPost<ScenarioChangeResponseApi>(
    `scenarios/${scenarioId}/changes`,
    request,
  );
}

export async function updateScenarioChange(
  scenarioId: number,
  changeId: number,
  request: UpdateScenarioChangeRequestApi,
): Promise<ScenarioChangeResponseApi> {
  return apiPut<ScenarioChangeResponseApi>(
    `scenarios/${scenarioId}/changes/${changeId}`,
    request,
  );
}

export async function deleteScenarioChange(
  scenarioId: number,
  changeId: number,
): Promise<void> {
  return apiDelete<void>(`scenarios/${scenarioId}/changes/${changeId}`);
}

// Scenario Shares CRUD
export async function shareScenario(
  scenarioId: number,
  request: ShareScenarioRequestApi,
): Promise<void> {
  return apiPost<void>(`scenarios/${scenarioId}/shares`, request);
}

export async function updateScenarioShare(
  scenarioId: number,
  userId: string,
  request: UpdateScenarioShareRequestApi,
): Promise<void> {
  return apiPut<void>(`scenarios/${scenarioId}/shares/${userId}`, request);
}

export async function deleteScenarioShare(
  scenarioId: number,
  userId: string,
): Promise<void> {
  return apiDelete<void>(`scenarios/${scenarioId}/shares/${userId}`);
}

// Scenario Comments
export async function getScenarioComments(
  scenarioId: number,
): Promise<ScenarioCommentListResponseApi> {
  return apiGet<ScenarioCommentListResponseApi>(
    `scenarios/${scenarioId}/comments`,
  );
}

export async function createScenarioComment(
  scenarioId: number,
  request: CreateScenarioCommentRequestApi,
): Promise<ScenarioCommentResponseApi> {
  return apiPost<ScenarioCommentResponseApi>(
    `scenarios/${scenarioId}/comments`,
    request,
  );
}

// Scenario Favorites
export async function toggleScenarioFavorite(
  scenarioId: number,
): Promise<{ isFavorite: boolean }> {
  return apiPost<{ isFavorite: boolean }>(
    `scenarios/${scenarioId}/favorite`,
    {},
  );
}

// Scenario Versions — reads go through GraphQL (getScenarioVersionsGraphql);
// creating and restoring versions stay on REST.
export async function createScenarioVersion(
  scenarioId: number,
  request: CreateScenarioVersionRequestApi,
): Promise<ScenarioVersionResponseApi> {
  return apiPost<ScenarioVersionResponseApi>(
    `scenarios/${scenarioId}/versions`,
    request,
  );
}

// Permanently overwrites the scenario's adjustments with the selected version's
// snapshot, then recomputes/refreshes metrics server-side.
export async function restoreScenarioVersion(
  scenarioId: number,
  versionId: number,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/versions/${versionId}/restore`,
    {},
  );
}

// Scenario Dependencies CRUD
export async function getScenarioDependencies(
  scenarioId: number,
): Promise<ScenarioDependencyResponseApi[]> {
  return apiGet<ScenarioDependencyResponseApi[]>(
    `scenarios/${scenarioId}/dependencies`,
  );
}

export async function createScenarioDependency(
  scenarioId: number,
  request: CreateScenarioDependencyRequestApi,
): Promise<ScenarioDependencyResponseApi> {
  return apiPost<ScenarioDependencyResponseApi>(
    `scenarios/${scenarioId}/dependencies`,
    request,
  );
}

export async function deleteScenarioDependency(
  scenarioId: number,
  dependencyId: number,
): Promise<void> {
  return apiDelete<void>(
    `scenarios/${scenarioId}/dependencies/${dependencyId}`,
  );
}

// Scenario Approval Workflow
export async function submitForApproval(
  scenarioId: number,
  request?: { skipReview?: boolean; coveringNote?: string },
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/submit`,
    request ?? {},
  );
}

export async function markReviewed(
  scenarioId: number,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/mark-reviewed`,
    {},
  );
}

export async function escalateToApproval(
  scenarioId: number,
  request?: { coveringNote?: string },
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/escalate`,
    request ?? {},
  );
}

export async function approveScenario(
  scenarioId: number,
  request: ApproveScenarioRequestApi,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/approve`,
    request,
  );
}

export async function rejectScenario(
  scenarioId: number,
  request: RejectScenarioRequestApi,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/reject`,
    request,
  );
}

export async function withdrawApproval(
  scenarioId: number,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/withdraw`,
    {},
  );
}

export async function requestChangesOnScenario(
  scenarioId: number,
  request: RequestChangesRequestApi,
): Promise<ScenarioResponseApi> {
  return apiPost<ScenarioResponseApi>(
    `scenarios/${scenarioId}/approval/request-changes`,
    request,
  );
}

export async function getScenarioApprovals(
  scenarioId: number,
): Promise<ScenarioApprovalResponseApi[]> {
  return apiGet<ScenarioApprovalResponseApi[]>(
    `scenarios/${scenarioId}/approval`,
  );
}

// Pending approvals for current user
export async function getPendingApprovals(): Promise<ScenarioResponseApi[]> {
  return apiGet<ScenarioResponseApi[]>("scenarios/pending-approvals");
}

// Scenario Forecast
export async function getScenarioForecast(
  scenarioId: number,
  period: string = "90d",
): Promise<ScenarioForecastResponseApi> {
  return apiGet<ScenarioForecastResponseApi>(
    `scenarios/${scenarioId}/forecast?period=${encodeURIComponent(period)}`,
  );
}

// Scenario Refresh — re-pin + re-materialise against the latest base forecast.
// Returns 202 Accepted with the claimed refresh status ("Updating" or "AlreadyUpdating").
export interface ScenarioRefreshResponseApi {
  scenarioId: number;
  status: string;
}

export async function refreshScenario(
  scenarioId: number,
): Promise<ScenarioRefreshResponseApi> {
  return apiPost<ScenarioRefreshResponseApi>(
    `scenarios/${scenarioId}/refresh`,
    {},
  );
}

// Scenario Metrics
export async function getScenarioMetrics(
  scenarioId: number,
  period: string = "90d",
): Promise<ScenarioMetricsResponseApi> {
  return apiGet<ScenarioMetricsResponseApi>(
    `scenarios/${scenarioId}/metrics?period=${encodeURIComponent(period)}`,
  );
}
